import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";
import { useMutation, useQuery } from "@apollo/client";

import { components } from "../../components";
import AuthLayout from "../../services/auth/jwt/withAuth";
import AppButtonGroup from "../../components/apps/AppButtonGroup";
import AppBottomDialog from "../../components/apps/AppBottomDialog";
import TimerCard from "../../components/home/TimerCard";
import { AppState, dispatch, useSelector } from "../../store/store";
import { homeActions } from "../../store/slices/homeSlice";
import TradeHeader from "../../components/shared/TradeHeader";
import AssetButton from "../../components/home/assets/AssetButton";
import AddAssetButton from "../../components/home/assets/AddAssetButton";
import { showErrorToast } from "../../components/apps/AppError";
import AppDialog from "../../components/apps/AppDialog";
import { theme } from "../../constants";
import AssetList from "../../components/home/assets/AssetList";
import { AssetSymbol, Interval, Timer } from "../../types/models/AssetSymbol";
import { getSymbolLogo } from "../../utils/tradeUtils";
import {
  GET_ASSET_SYMBOL_LIST,
  GET_SYMBOL_TIMERS,
} from "../../graphql/queries/trade";
import { DownIcon, UpIcon } from "../../constants/icons";
import { colors } from "../../constants/colors";
import { TRADE } from "../../graphql/mutations/trade";
import {
  useQueryCountMyTrades,
  useQueryProfile,
} from "../../graphql/apollo/useQuery";
import AppTextFieldNoForm from "../../components/apps/AppTextFieldNoForm";
import GraphInterval from "../../components/home/GraphInterval";
import GraphIcon from "../../components/home/GraphIcon";
import { currentBranch } from "../../constants/constants";

export const Home: React.FC = () => {
  const {
    timerDialog,
    amountDialog,
    assetDialog,
    selectedSymbols,
    activeSymbol,
    amount,
    currentTimer,
    interval,
    graphDialog,
    graphStyle,
  } = useSelector<AppState["home"]>(({ home }) => home);
  const { loading } = useQuery(GET_SYMBOL_TIMERS, {
    fetchPolicy: "cache-first",
    onError: (error) => showErrorToast(error.message),
    onCompleted(data) {
      const assetTimers: Timer[] = data?.symbolTimers ?? [];
      const currentTimerExist = assetTimers.find(
        (item) => item.id === currentTimer?.id,
      );
      if (!currentTimer) {
        const defaultCurrentTimer: Timer | undefined =
          assetTimers.length > 0 ? assetTimers[0] : undefined;
        if (defaultCurrentTimer) {
          dispatch(
            homeActions.setCurrentAssetTimer({ timer: defaultCurrentTimer }),
          );
        }
      } else if (!currentTimerExist) {
        dispatch(homeActions.setCurrentAssetTimer({ timer: assetTimers[0] }));
      }
      dispatch(homeActions.setAssetTimers({ assetTimers }));
    },
  });
  const { refetch: refetchCount } = useQueryCountMyTrades();
  const [timerSymbol, setTimerSymbol] = React.useState<string>("");
  const { t } = useTranslation();
  const { refetch: refetchProfile } = useQueryProfile();

  const { pathname } = useLocation();
  const toggleTimerDialog = () => {
    dispatch(homeActions.toggleTimerDialog());
  };
  const toggleAmountDialog = () => dispatch(homeActions.toggleAmountDialog());

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const renderBottomTabBar = (): JSX.Element => {
    return <components.BottomTabBar />;
  };

  const { data: { symbolList } = {} } = useQuery(GET_ASSET_SYMBOL_LIST, {
    fetchPolicy: "cache-first",
    onError: (error) => showErrorToast(error.message),
  });

  const [tradePosition] = useMutation(TRADE, {
    onError: (error) => showErrorToast(error.message),
    onCompleted(data) {
      if (data.trade) {
        refetchCount();
        refetchProfile();
      }
    },
  });

  const toggleAssetDialog = () => dispatch(homeActions.toggleAssetDialog());
  const toggleGraphDialog = () => dispatch(homeActions.toggleGraphDialog());

  const handleClickAsset = (asset: AssetSymbol) => {
    toggleAssetDialog();
    dispatch(homeActions.onSelectAsset({ asset, rearange: true }));
    setTimerSymbol(asset.symbol);
  };

  const handleClickSelectAsset = (asset: AssetSymbol) => {
    dispatch(homeActions.onSelectAsset({ asset, rearange: false }));
    setTimerSymbol(asset.symbol);
  };
  const handleRemoveSelectAsset = (
    e: React.MouseEvent<HTMLDivElement>,
    asset: AssetSymbol,
  ) => {
    e.stopPropagation();
    dispatch(homeActions.onRemoveSelectedAsset({ asset }));
  };

  const increaseAmount = () => dispatch(homeActions.increaseAmount());
  const decreaseAmount = () => dispatch(homeActions.decreaseAmount());

  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = Number.parseInt(e.target.value);
    dispatch(homeActions.selectAmount({ amount }));
  };
  const increaseTimer = () => dispatch(homeActions.increaseTimer());
  const decreaseTimer = () => dispatch(homeActions.decreaseTimer());

  useEffect(() => {
    setTimerSymbol(activeSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSymbol]);

  const handleTrade = (positionType: string) => {
    tradePosition({
      variables: {
        input: {
          positionType,
          symbol: activeSymbol,
          timer: currentTimer?.id,
          openingTime: new Date(),
          investment: isNaN(Number(amount)) ? 1 : amount,
          branch: currentBranch,
        },
      },
    });
  };

  const handleSetInterval = (interval: Interval) => {
    dispatch(homeActions.setInterval(interval));
    dispatch(homeActions.setActiveSymbol(activeSymbol));
  };

  return (
    <AuthLayout>
      <main className="home">
        <TradeHeader />
        <div className="home-trade__assets">
          <div style={{ display: "flex", columnGap: "5px", width: "100%" }}>
            {selectedSymbols.map((asset: AssetSymbol) => {
              return (
                <AssetButton
                  key={asset.symbol}
                  label={asset.name}
                  symbol={asset.symbol}
                  handleRemove={(e: React.MouseEvent<HTMLDivElement>) =>
                    handleRemoveSelectAsset(e, asset)
                  }
                  onClick={() => handleClickSelectAsset(asset)}
                  logo={getSymbolLogo(asset.symbol, asset.logo)}
                  isActive={asset.symbol === activeSymbol}
                />
              );
            })}
          </div>
          <AddAssetButton onClick={toggleAssetDialog} />
        </div>
        <div className="home-trade__graph">
          <Typography sx={{ px: 1, py: 2 }}>{t("expectReturn")}</Typography>
          <AdvancedRealTimeChart
            height={650}
            hide_side_toolbar
            hide_top_toolbar
            allow_symbol_change={false}
            theme="dark"
            autosize
            symbol={activeSymbol}
            timezone="Asia/Bangkok"
            interval={interval}
            style={graphStyle}
            // range="3M"
          />
        </div>
        <div className="home-trade__panel">
          <div className="home-trade__panel-time">
            <Button variant="text" color="inherit" onClick={toggleGraphDialog}>
              <GraphIcon graph={graphStyle} />
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => handleSetInterval("D")}
            >
              All
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => handleSetInterval("60")}
            >
              1H
            </Button>
            <Button
              variant="text"
              color="inherit"
              sx={{ textTransform: "none" }}
              onClick={() => handleSetInterval("30")}
            >
              30m
            </Button>
            <Button
              sx={{ textTransform: "none" }}
              variant="text"
              color="inherit"
              onClick={() => handleSetInterval("15")}
            >
              15m
            </Button>
            <Button
              sx={{ textTransform: "none" }}
              variant="text"
              color="inherit"
              onClick={() => handleSetInterval("5")}
            >
              5m
            </Button>
          </div>
          <div className="home-trade__panel-button">
            <Button
              variant="contained"
              color="error"
              fullWidth
              sx={{ background: colors.red, borderRadius: 2.5 }}
              onClick={() => handleTrade("DOWN")}
            >
              <DownIcon size={25} />
              <Typography
                variant="body1"
                color="white"
                sx={{ textTransform: "capitalize", p: 1 }}
              >
                {t("down")}
              </Typography>
            </Button>
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{ background: colors.green, borderRadius: 2.5 }}
              onClick={() => handleTrade("UP")}
            >
              <UpIcon size={25} />
              <Typography
                variant="body1"
                color="white"
                sx={{ textTransform: "capitalize", p: 1 }}
              >
                {t("up")}
              </Typography>
            </Button>
          </div>
          <div className="home-trade__panel-amount">
            <Grid container columnSpacing={1}>
              <Grid item xs={6}>
                <AppButtonGroup
                  onDecrease={decreaseTimer}
                  onIncrease={increaseTimer}
                  onToggleDialog={toggleTimerDialog}
                  label={t("timer")}
                  text={currentTimer ? currentTimer?.label : ""}
                />
              </Grid>
              <Grid item xs={6}>
                <AppButtonGroup
                  onDecrease={decreaseAmount}
                  onIncrease={increaseAmount}
                  onToggleDialog={toggleAmountDialog}
                  label={`${t("amount")} ($)`}
                  text={amount}
                  isNumSymbol
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </main>
      {/* Dialog */}
      <AppDialog
        title={t("chooseAsset")}
        open={assetDialog}
        onClose={toggleAssetDialog}
        fullScreen
        backgroundColor={theme.colors.backgroundColorDark}
        leftHeader
      >
        <AssetList
          assets={symbolList ?? []}
          onClick={(asset: AssetSymbol) => handleClickAsset(asset)}
        />
      </AppDialog>
      <AppBottomDialog
        open={timerDialog}
        handleClose={toggleTimerDialog}
        title={t("timer")}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
          }}
        >
          <TimerCard symbol={timerSymbol} loading={loading} />
          <Button
            variant="contained"
            color="warning"
            onClick={toggleTimerDialog}
            size="large"
            fullWidth
            sx={{
              marginTop: "20px",
              padding: "12px 10px",
              borderRadius: "10px",
            }}
          >
            {t("confirm")}
          </Button>
        </div>
      </AppBottomDialog>
      <AppBottomDialog
        open={amountDialog}
        handleClose={toggleAmountDialog}
        title={t("amount")}
      >
        <div className="home__amount-card_container">
          <AppTextFieldNoForm
            placeholder={t("enterAmount")}
            name="amount"
            themeColor="dark"
            type="number"
            value={amount}
            onChange={handleChangeAmount}
          />
        </div>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="warning"
            onClick={toggleAmountDialog}
            size="large"
            fullWidth
            sx={{ padding: "12px 10px", borderRadius: "10px" }}
          >
            {t("confirm")}
          </Button>
        </Box>
      </AppBottomDialog>
      <AppBottomDialog
        open={graphDialog}
        handleClose={toggleGraphDialog}
        title={t("graphType")}
      >
        <GraphInterval />
      </AppBottomDialog>
      {renderBottomTabBar()}
    </AuthLayout>
  );
};
